import {colorsNames} from '~utils/colors-utils';

export const invertColor = (hexTripletColor: string) => {
  hexTripletColor = colorNameToHex(hexTripletColor) || hexTripletColor;
  // @ts-ignore
  let color = hexTripletColor.match(/[0-9a-fA-F]+/)[0];
  // eslint-disable-next-line no-bitwise
  const colorInt = 0xFFFFFF ^ parseInt(color, 16);
  color = '#' + ('000000' + colorInt.toString(16)).slice(-6);

  return color;
};

export const constrastMap = (color: string): string => {
  return isDark(toRgb(color)) ? '#9e9e9e' : '#616161';
};

export const lightOrDark = (color: string): string => {
  const [r, g, b] = toRgb(color);
  const hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));
  return (hsp > 127.5) ? '#eee' : '#333';
};

export const transparentize = (hexTripletColor: string, opacity: number = 1) => {
  hexTripletColor = colorNameToHex(hexTripletColor) || hexTripletColor;
  // @ts-ignore
  const color = hexTripletColor.match(/[0-9a-fA-F]+/)[0];
  opacity = (opacity > 1 ? 1 : opacity < 0 ? 0 : opacity);
  const hexOpacity = (parseInt(`${255 * opacity}`, 10)).toString(16);
  return '#' + color + (hexOpacity.length === 1 ? '0' + hexOpacity : hexOpacity);
};

export const colorLuminance = (hex: string, lum: number) => {
  hex = String(hex).replace(/[^0-9a-f]/gi, '');
  if (hex.length < 6) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  lum = lum || 0;
  let rgb = '#';
  let c;
  for (let i = 0; i < 3; i++) {
    c = parseInt(hex.slice(i * 2, (i * 2) + 2), 16);
    c = Math.round(Math.min(Math.max(0, c + ((c ? c : 1) * lum)), 255)).toString(16);
    rgb += ('00' + c).slice(c.length);
  }
  return rgb;
};

export const light = (cor: string) => {
  cor = cor.length < 5 ? cor.slice(1).replace(/./g, '$&$&') : cor.slice(1);
  const color = +(`0x${cor}`);
  // eslint-disable-next-line no-bitwise
  const r = color >> 16;
  // eslint-disable-next-line no-bitwise
  const g = color >> 8 & 255;
  // eslint-disable-next-line no-bitwise
  const b = color & 255;
  const hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));
  return hsp > 127.5;
};

const poly: { [k: string]: RGB[] } = {};

export const palette = (color: string, type: PALETTE): [string, boolean, PALETTE] => {
  const amount = amountPalette[type];
  const colorBase = '#ffffff';
  let rgb = toRgb(color);
  const rgbBase = (type.startsWith('A') || type > '500') ? multiply(rgb, rgb) : toRgb(colorBase);
  if (type.startsWith('A')) {
    if (!poly[color]) {
      poly[color] = polyad(rgb, 4).map(([r, g, b]) =>
        [Math.round(r), Math.round(g), Math.round(b)]);
    }
    rgb = poly[color][3];
  }

  let res = mix(rgbBase, rgb, amount);
  if (type.startsWith('A')) {
    let s;
    let l;
    if (type === 'A100' || type === 'A200') {
      s = 80;
      l = (type === 'A100') ? 65 : 55;
    } else {
      s = 100;
      l = (type === 'A400') ? 45 : 40;
    }
    res = saturate(res, s);
    res = lighten(res, l);
  }

  return [rgbToHex(res), isLight(res), type];
};

export const toHsl = (color: string): HSL => {
  return rgbToHsl(toRgb(color));
};

export const toHslString = (color: string) => {
  let [h, s, l] = toHsl(color);
  h = Math.round((h ?? 0) * 360);
  s = Math.round(s * 100);
  l = Math.round(l * 100);
  return `hsl(${h}, ${s}%, ${l}%)`;
};

const saturate = (color: RGB, amount: number): RGB => {
  amount = amount === 0 ? 0 : amount || 10;
  const hsl = rgbToHsl(color);
  hsl[1] += amount / 100;
  hsl[1] = Math.min(1, Math.max(0, hsl[1]));
  const [r, g, b] = hslToRgb(hsl);
  return [r, g, b];
};

const lighten = (color: RGB, amount: number): RGB => {
  amount = amount === 0 ? 0 : amount || 10;
  const hsl = rgbToHsl(color);
  hsl[2] += amount / 100;
  hsl[2] = Math.min(1, Math.max(0, hsl[2]));
  const [r, g, b] = hslToRgb(hsl);
  return [r, g, b];
};

const polyad = (color: RGB, num: number) => {
  const [h, s, l] = rgbToHsl(color);
  const result = [color];
  const step = 360 / num;
  for (let i = 1; i < num; i++) {
    result.push(hslToRgb([(h + i * step) % 360, s, l]));
  }
  return result;
};

const toRgb = (color: string): RGB => {
  const rgb = (cor: string): RGB => {
    const [_, x, y, z] = (cor.match(regexRGB1) ?? cor.match(regexRGB2)) as unknown as number[];
    return [x, y, z];
  };
  const noRgb = (cor: string): RGB => {// @ts-ignore
    cor = +('0x' + cor.slice(1).replace(cor.length < 5 && /./g, '$&$&'));
    // @ts-ignore
    // eslint-disable-next-line no-bitwise
    return [cor >> 16, cor >> 8 & 255, cor & 255];
  };
  const [r, g, b] = (color.match(/^rgb/)) ? rgb(color) : noRgb(color);
  return [Math.round(r), Math.round(g), Math.round(b)];
};

const rgbToHex = (color: RGB, allow3Char?: boolean): string => {
  const hex = RANGE_3.map(i => pad2(Math.round(color[i]).toString(16)));
  if (allow3Char && hex[0].charAt(0) === hex[0].charAt(1) &&
    hex[1].charAt(0) === hex[1].charAt(1) && hex[2].charAt(0) === hex[2].charAt(1)) {
    return hex[0].charAt(0) + hex[1].charAt(0) + hex[2].charAt(0);
  }
  return '#' + hex.join('');
};

const pad2 = (c: string): string => c.length === 1 ? '0' + c : '' + c;

const rgbToHsl = ([r, g, b]: RGB): RGB => {
  r = bound01(r, 255);
  g = bound01(g, 255);
  b = bound01(b, 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s;
  const l = (max + min) / 2;
  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) {
      h = (g - b) / d + (g < b ? 6 : 0);
    } else if (max === g) {
      h = (b - r) / d + 2;
    } else if (max === b) {
      h = (r - g) / d + 4;
    }
    h = h / 6;
  }
  return [(h ?? 0) * 360, s, l];
};

const hslToRgb = ([h, s, l]: RGB): RGB => {
  const sString = convertToPercentage(s);
  const lString = convertToPercentage(l);
  let r;
  let g;
  let b;
  h = bound01(h, 360);
  s = bound01(sString, 100);
  l = bound01(lString, 100);
  const hue2rgb = ([p, q, t]: HUE): number => {
    if (t < 0) {
      t += 1;
    }
    if (t > 1) {
      t -= 1;
    }
    if (t < 1 / 6) {
      return p + (q - p) * 6 * t;
    }
    if (t < 1 / 2) {
      return q;
    }
    if (t < 2 / 3) {
      return p + (q - p) * (2 / 3 - t) * 6;
    }
    return p;
  };
  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb([p, q, h + 1 / 3]);
    g = hue2rgb([p, q, h]);
    b = hue2rgb([p, q, h - 1 / 3]);
  }
  return [r * 255, g * 255, b * 255];
};

const bound01 = (n: number | string, max: number): number => {
  if (isOnePointZero(n)) {
    n = '100%';
  }
  const processPercent = isPercentage(n);
  const _n = Math.min(max as number, Math.max(0, parseFloat(n as string)));
  if (Math.abs(_n - max) < 0.000001) {
    return 1;
  }
  return _n % max / parseFloat(max + '');
};

const isOnePointZero = (n: unknown) => typeof n === 'string' && n.indexOf('.') !== -1 && parseFloat(n) === 1;

const isPercentage = (n: unknown) => typeof n === 'string' && n.indexOf('%') !== -1;

const convertToPercentage = (n?: number): string => n && n <= 1 ? n * 100 + '%' : n + '';

const getBrightness = ([r, g, b]: RGB) => (r * 299 + g * 587 + b * 114) / 1000;

const isDark = (rgb: RGB) => getBrightness(rgb) < 128;

const isLight = (rgb: RGB) => !isDark(rgb);

const multiply = (rgb1: RGB, rgb2: RGB): RGB => {
  const fn = (pos: number) => Math.floor((rgb1[pos] * rgb2[pos]) / 255);
  const [r, g, b] = RANGE_3.map(fn);
  return [r, g, b];
};

const mix = (color1: string | RGB, color2: string | RGB, amount: number): RGB => {
  const [rgb1, rgb2] = [color1, color2].map(c => typeof c === 'string' ? toRgb(c) : c);
  const p = (amount === 0 ? 0 : amount || 50) / 100;
  const fn = (pos: number) => (rgb2[pos] - rgb1[pos]) * p + rgb1[pos];
  const [r, g, b] = RANGE_3.map(fn);
  return [r, g, b];
};

export const colorNameToHex = (color: string) => {
  const key = color.toLowerCase();
  return typeof colorsNames[key] !== 'undefined' ? colorsNames[key] : undefined;
};

const RANGE_3 = [0, 1, 2];
const regexRGB1 = /^(?:rgb|rgba)?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/;
const regexRGB2 = /^(?:rgb|rgba)? (\d+) \s*(\d+) \s*(\d+)(?: \s*(\d+(?:\.\d+)?))?$/;

const amountPalette: {[k: string]: number} = {
  50: 12,
  100: 30,
  200: 50,
  300: 70,
  400: 85,
  500: 100,
  600: 87,
  700: 70,
  800: 54,
  900: 25,
  A100: 15,
  A200: 15,
  A400: 15,
  A700: 15,
};

export const PaletteKeys = Object.keys(amountPalette) as PALETTE[];

type PALETTE = '50' | '100' | '200' | '300' | '400' | '500'
  | '600' | '700' | '800' | '900' | 'A100' | 'A200' | 'A400' | 'A700';

type N_3 = [number, number, number];
type RGB = N_3;
type HSL = N_3;
type HUE = N_3;
